import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Calendar, Lock, LockOpen, MapPin, Printer, Thermometer } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCertificateStatus, formatCertificateDate } from '../utils/certificateStatus';
import { printProbeTag } from '../utils/printProbeTag';
import type { CalibrationCertificate } from '../types';

interface CalibrationCertificateListViewProps {
  certificates: CalibrationCertificate[];
  isLoading?: boolean;
  onCertificateClick?: (cert: CalibrationCertificate) => void;
  selectedIds?: Set<string>;
  onSelect?: (id: string, selected: boolean) => void;
  onToggleLock?: (cert: CalibrationCertificate) => void;
}

function CertificateCard({
  cert,
  onClick,
  isSelected,
  onSelect,
  onToggleLock,
}: {
  cert: CalibrationCertificate;
  onClick?: () => void;
  isSelected?: boolean;
  onSelect?: (selected: boolean) => void;
  onToggleLock?: () => void;
}) {
  const { t } = useTranslation('intelligence');
  const status = getCertificateStatus(cert.daysUntilDue, t);

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:shadow-md border',
        status.bgColor,
        isSelected && 'ring-2 ring-primary'
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        {/* Header: Cert # and Status */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {onSelect && (
              <input
                type="checkbox"
                aria-label={t('calibration.selectCert', 'Select certificate {{certNumber}}', { certNumber: cert.certNumber })}
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onSelect(e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                className="rounded border-gray-300"
              />
            )}
            <span className="font-mono text-sm font-bold">{cert.certNumber}</span>
            <button
              type="button"
              title="Print Probe Tag"
              aria-label="Print Probe Tag"
              onClick={(e) => {
                e.stopPropagation();
                printProbeTag(cert);
              }}
              className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
            >
              <Printer className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            {onToggleLock && (
              <button
                type="button"
                title={
                  cert.locked
                    ? 'Unlock — requires calibrationrecords.modify permission'
                    : 'Lock — requires calibrationrecords.modify permission'
                }
                aria-label={cert.locked ? 'Unlock certificate' : 'Lock certificate'}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleLock();
                }}
                className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
              >
                {cert.locked ? (
                  <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <LockOpen className="h-3.5 w-3.5 text-muted-foreground/50" />
                )}
              </button>
            )}
          </div>
          <Badge variant={status.variant as 'destructive' | 'default' | 'secondary' | 'outline'}>
            {status.label}
          </Badge>
        </div>

        {/* Organisation */}
        <div className="text-sm font-medium text-foreground mb-3">
          {cert.organisationName || t('calibration.unknownOrg', 'Unknown Organisation')}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-3" />

        {/* Sensor/Equipment Info */}
        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <Thermometer className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="font-medium truncate">
                {cert.streamName || cert.equipmentName || t('calibration.unknown', 'Unknown')}
              </div>
              {cert.streamSerialNumber && (
                <div className="text-xs text-muted-foreground">
                  S/N: {cert.streamSerialNumber}
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="truncate">
                {cert.siteName || '-'}
              </div>
              {cert.locationName && (
                <div className="text-xs text-muted-foreground truncate">
                  {cert.locationName}
                </div>
              )}
            </div>
          </div>

          {/* Due Date */}
          <div className="flex items-start gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <div className={cn('font-medium', status.textColor)}>
                {t('calibration.dueLabel', 'Due')}: {formatCertificateDate(cert.dueDate)}
              </div>
              {cert.daysUntilDue !== null && (
                <div className={cn('text-xs', status.textColor)}>
                  {cert.daysUntilDue < 0 ? (
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {Math.abs(cert.daysUntilDue)} {t('calibration.daysOverdue', 'days overdue')}
                    </span>
                  ) : (
                    <span>
                      {cert.daysUntilDue} {t('calibration.daysRemaining', 'days remaining')}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SkeletonCard() {
  return (
    <Card className="border">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-16" />
        </div>
        <Skeleton className="h-4 w-32 mb-3" />
        <div className="border-t my-3" />
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-28" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function CalibrationCertificateListView({
  certificates,
  isLoading,
  onCertificateClick,
  selectedIds,
  onSelect,
  onToggleLock,
}: CalibrationCertificateListViewProps) {
  const { t } = useTranslation('intelligence');

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[...Array(8)].map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (certificates.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {t('calibration.noResults', 'No certificates found matching your criteria.')}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {certificates.map((cert) => (
        <CertificateCard
          key={cert.id}
          cert={cert}
          onClick={() => onCertificateClick?.(cert)}
          isSelected={selectedIds?.has(cert.id)}
          onSelect={onSelect ? (selected) => onSelect(cert.id, selected) : undefined}
          onToggleLock={onToggleLock ? () => onToggleLock(cert) : undefined}
        />
      ))}
    </div>
  );
}
